import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message, 
  Popconfirm,
  Card,
  Typography,
  Tag,
  Tooltip,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined,
  ReloadOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  DollarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { giftService, GiftGiver, CreateGiftGiverRequest, UpdateGiftGiverRequest } from '../../../services/giftService';
import styles from './GiftGiversPage.module.css';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const GiftGiversPage: React.FC = () => {
  const navigate = useNavigate();
  const [givers, setGivers] = useState<GiftGiver[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingGiver, setEditingGiver] = useState<GiftGiver | null>(null);
  const [form] = Form.useForm();
  const [filterName, setFilterName] = useState<string>('');
  const [filterRelationship, setFilterRelationship] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<boolean | undefined>();
  const [giversWithStats, setGiversWithStats] = useState<GiftGiver[]>([]);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  
  // Bỏ client-side filtering vì đã có server-side filtering
  const filteredGivers = giversWithStats;

  const fetchGivers = async (page: number = 1, size: number = 10) => {
    try {
      setLoading(true);
      
      // Prepare API parameters
      const apiParams: any = {
        page,
        limit: size,
      };

      // Add search filters if any
      if (filterName) {
        apiParams.search = filterName;
      }
      if (filterRelationship) {
        apiParams.relationship = filterRelationship;
      }
      if (filterStatus !== undefined) {
        apiParams.is_active = filterStatus;
      }

      const response = await giftService.getGiftGivers(apiParams);
      setGivers(response.data);
      
      // Fetch gift statistics for each giver
      const giversWithGiftStats = await Promise.all(
        response.data.map(async (giver: GiftGiver) => {
          try {
            const giftsResponse = await giftService.getGifts({ giver_id: giver.id });
            const totalAmount = giftsResponse.data.reduce((sum: number, gift: any) => sum + (gift.amount || 0), 0);
            const giftCount = giftsResponse.data.length;
            return {
              ...giver,
              total_gift_amount: totalAmount,
              gift_count: giftCount
            };
          } catch (error) {
            return {
              ...giver,
              total_gift_amount: 0,
              gift_count: 0
            };
          }
        })
      );
      
      setGiversWithStats(giversWithGiftStats);
      
      // Update pagination info
      if (response.pagination) {
        setTotal(response.pagination.total);
        setCurrentPage(response.pagination.page);
        setPageSize(response.pagination.limit);
      }
    } catch (error) {
      message.error('Không thể tải danh sách người mừng');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGivers(1, pageSize);
  }, []);

  const handleCreate = () => {
    setEditingGiver(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: GiftGiver) => {
    setEditingGiver(record);
    form.setFieldsValue({
      full_name: record.full_name,
      relationship: record.relationship,
      phone: record.phone,
      email: record.email,
      address: record.address,
      social_media: record.social_media,
      notes: record.notes
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await giftService.deleteGiftGiver(id);
      message.success('Xóa người mừng thành công');
      fetchGivers(currentPage, pageSize);
    } catch (error) {
      message.error('Không thể xóa người mừng');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingGiver) {
        await giftService.updateGiftGiver(editingGiver.id, values);
        message.success('Cập nhật người mừng thành công');
      } else {
        await giftService.createGiftGiver(values);
        message.success('Tạo người mừng thành công');
      }

      setModalVisible(false);
      fetchGivers(currentPage, pageSize);
    } catch (error) {
      message.error('Có lỗi xảy ra');
    }
  };

  const getRelationshipLabel = (relationship: string) => {
    const labels: Record<string, string> = {
      family_internal: 'Gia đình nội',
      family_external: 'Gia đình ngoại',
      friend_bride: 'Bạn cô dâu',
      friend_groom: 'Bạn chú rể',
      colleague: 'Đồng nghiệp',
      business_partner: 'Đối tác',
      other: 'Khác'
    };
    return labels[relationship] || relationship;
  };

  const getRelationshipColor = (relationship: string) => {
    const colors: Record<string, string> = {
      family_internal: 'red',
      family_external: 'orange',
      friend_bride: 'pink',
      friend_groom: 'blue',
      colleague: 'green',
      business_partner: 'purple',
      other: 'default'
    };
    return colors[relationship] || 'default';
  };

  const columns = [
    {
      title: 'STT',
      key: 'stt',
      width: 60,
      render: (_: any, __: any, index: number) => {
        const stt = (currentPage - 1) * pageSize + index + 1;
        return <span style={{ fontWeight: 'bold' }}>{stt}</span>;
      }
    },
    {
      title: 'Họ và tên',
      dataIndex: 'full_name',
      key: 'full_name',
      width: 180,
      render: (text: string, record: any) => (
        <Space direction="vertical" size="small">
          <strong style={{ fontSize: '14px', color: '#333' }}>{text}</strong>
          {record.phone && (
            <span style={{ fontSize: '11px', color: '#666' }}>
              📞 {record.phone}
            </span>
          )}
        </Space>
      )
    },
    {
      title: 'Mối quan hệ',
      dataIndex: 'relationship',
      key: 'relationship',
      width: 120,
      render: (relationship: string) => (
        <Tag 
          color={getRelationshipColor(relationship)}
          style={{ 
            fontWeight: 'bold',
            fontSize: '11px',
            padding: '2px 8px'
          }}
        >
          {getRelationshipLabel(relationship)}
        </Tag>
      )
    },
    {
      title: 'Tổng tiền mừng',
      key: 'total_gifts',
      width: 150,
      render: (_: any, record: any) => {
        const formatCurrency = (amount: number) => {
          return new Intl.NumberFormat('vi-VN', {
            style: 'currency',
            currency: 'VND'
          }).format(amount);
        };
        
        const totalAmount = record.total_gift_amount || 0;
        const giftCount = record.gift_count || 0;
        
        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space>
              <DollarOutlined style={{ color: '#52c41a' }} />
              <strong style={{ 
                color: totalAmount > 0 ? '#3f8600' : '#666',
                fontSize: '14px'
              }}>
                {formatCurrency(totalAmount)}
              </strong>
            </Space>
            <span style={{ 
              fontSize: '11px', 
              color: '#999',
              display: 'block'
            }}>
              {giftCount > 0 ? `${giftCount} món quà` : 'Chưa có món quà nào'}
            </span>
          </Space>
        );
      },
      sorter: (a: any, b: any) => (a.total_gift_amount || 0) - (b.total_gift_amount || 0)
    },
    {
      title: 'Thông tin liên hệ',
      key: 'contact',
      width: 200,
      render: (_: any, record: GiftGiver) => (
        <Space direction="vertical" size="small">
          {record.email && (
            <Space>
              <MailOutlined style={{ color: '#1890ff' }} />
              <span style={{ fontSize: '12px' }}>{record.email}</span>
            </Space>
          )}
          {record.address && (
            <Space>
              <HomeOutlined style={{ color: '#52c41a' }} />
              <span style={{ 
                maxWidth: 150, 
                overflow: 'hidden', 
                textOverflow: 'ellipsis',
                fontSize: '12px'
              }}>
                {record.address}
              </span>
            </Space>
          )}
        </Space>
      )
    },
    {
      title: 'Mạng xã hội',
      dataIndex: 'social_media',
      key: 'social_media',
      render: (text: string) => text || '-'
    },
    {
      title: 'Ghi chú',
      dataIndex: 'notes',
      key: 'notes',
      render: (text: string) => text || '-',
      ellipsis: true
    },
    {
      title: 'Trạng thái',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      )
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 120,
      fixed: 'right' as const,
      render: (_: any, record: GiftGiver) => (
        <Space>
          <Tooltip title="Chỉnh sửa">
            <Button 
              type="link" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Bạn có chắc muốn xóa người mừng này?"
            onConfirm={() => handleDelete(record.id)}
            okText="Có"
            cancelText="Không"
          >
            <Tooltip title="Xóa">
              <Button 
                type="link" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className={styles.container}>
      <Card className={styles.header}>
        <div className={styles.headerContent}>
          <Title level={2} className={styles.title}>
          Quản lý người mừng
          </Title>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={() => fetchGivers(currentPage, pageSize)}
              loading={loading}
            >
              Làm mới
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Thêm người mừng
            </Button>
          </Space>
        </div>
      </Card>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <Input
              placeholder="Tìm kiếm theo tên, SĐT, email..."
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              onPressEnter={() => fetchGivers(1, pageSize)}
              allowClear
              prefix={<UserOutlined />}
            />
          </Col>
          <Col span={6}>
            <Select
              allowClear
              placeholder="Lọc theo mối quan hệ"
              style={{ width: '100%' }}
              value={filterRelationship}
              onChange={(value) => {
                setFilterRelationship(value);
                fetchGivers(1, pageSize);
              }}
            >
              <Option value="family_internal">Gia đình nội</Option>
              <Option value="family_external">Gia đình ngoại</Option>
              <Option value="friend_bride">Bạn cô dâu</Option>
              <Option value="friend_groom">Bạn chú rể</Option>
              <Option value="colleague">Đồng nghiệp</Option>
              <Option value="business_partner">Đối tác</Option>
              <Option value="other">Khác</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              allowClear
              placeholder="Lọc theo trạng thái"
              style={{ width: '100%' }}
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                fetchGivers(1, pageSize);
              }}
            >
              <Option value={true}>Hoạt động</Option>
              <Option value={false}>Không hoạt động</Option>
            </Select>
          </Col>
          <Col span={4}>
            <Button 
              onClick={() => { 
                setFilterName(''); 
                setFilterRelationship(undefined); 
                setFilterStatus(undefined); 
                fetchGivers(1, pageSize);
              }}
              style={{ width: '100%' }}
            >
              Xóa bộ lọc
            </Button>
          </Col>
        </Row>
        
        <Table
          columns={columns}
          dataSource={filteredGivers}
          rowKey="id"
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} người mừng`,
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
              fetchGivers(page, size);
            }
          }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={editingGiver ? 'Chỉnh sửa người mừng' : 'Thêm người mừng mới'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            relationship: 'other'
          }}
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="full_name"
                label="Họ và tên"
                rules={[{ required: true, message: 'Vui lòng nhập họ và tên' }]}
              >
                <Input placeholder="Nhập họ và tên đầy đủ" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="relationship"
                label="Mối quan hệ"
                rules={[{ required: true, message: 'Vui lòng chọn mối quan hệ' }]}
              >
                <Select placeholder="Chọn mối quan hệ">
                  <Option value="family_internal">Gia đình nội</Option>
                  <Option value="family_external">Gia đình ngoại</Option>
                  <Option value="friend_bride">Bạn cô dâu</Option>
                  <Option value="friend_groom">Bạn chú rể</Option>
                  <Option value="colleague">Đồng nghiệp</Option>
                  <Option value="business_partner">Đối tác</Option>
                  <Option value="other">Khác</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Số điện thoại"
              >
                <Input placeholder="Nhập số điện thoại" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
              >
                <Input placeholder="Nhập địa chỉ email" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="social_media"
                label="Mạng xã hội"
              >
                <Input placeholder="Facebook, Zalo, v.v." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Địa chỉ"
          >
            <TextArea 
              rows={2} 
              placeholder="Nhập địa chỉ chi tiết"
            />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Ghi chú"
          >
            <TextArea 
              rows={3} 
              placeholder="Nhập ghi chú hoặc click vào các nút bên dưới"
            />
          </Form.Item>
          <Form.Item label="Ghi chú mẫu">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {[
                'HR Potique', 'FB Potique', 'An Ninh Potique', 'Kỹ Thuật Potique',
                'FO Potique', 'Bếp Potique', 'KT Potique', 'Cứu hộ Potique',
                'Bell Potique', 'Học DL', 'Bạn C2', 'Bạn C3', 'Bạn C1',
                'Bạn thân', 'Trà Yoga', 'KS MaJestic'
              ].map((note, index) => (
                <Button
                  key={index}
                  size="small"
                  type="dashed"
                  onClick={() => {
                    const currentNotes = form.getFieldValue('notes') || '';
                    const newNotes = currentNotes ? `${currentNotes}, ${note}` : note;
                    form.setFieldsValue({ notes: newNotes });
                  }}
                  style={{ marginBottom: '4px' }}
                >
                  {note}
                </Button>
              ))}
            </div>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingGiver ? 'Cập nhật' : 'Thêm người mừng'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default GiftGiversPage; 